import React, { useState } from 'react';
import {
  Box, Container, Typography, Grid, TextField, Button,
  Checkbox, FormControlLabel, FormHelperText, Alert, Divider,
  Card, CardContent, Link, CircularProgress,
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import EmailIcon from '@mui/icons-material/Email';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://watchpassion.vercel.app/api';

const SOCIALS = [
  {
    icon: <InstagramIcon sx={{ fontSize: 32, color: '#E1306C' }} />,
    label: 'Instagram',
    sub: '@watch_passion',
    href: 'https://www.instagram.com/watch_passion/?hl=it',
  },
  {
    icon: <EmailIcon sx={{ fontSize: 32, color: '#951520' }} />,
    label: 'Email',
    sub: 'Info@watchpassion.net',
    href: 'mailto:Info@watchpassion.net',
  },
];

const ContactPage = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', gdpr: false });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.email) e.email = t('contact.errorEmail');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t('contact.errorEmailInvalid');
    if (!form.gdpr) e.gdpr = t('contact.errorGdpr');
    return e;
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', phone: '', message: '', gdpr: false });
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
          {t('contact.title')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          {t('contact.subtitle')}
        </Typography>
      </Box>

      <Grid container spacing={{ xs: 3, md: 6 }}>
        {/* Colonna sinistra – social */}
        <Grid item xs={12} md={4}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {t('contact.followUs')}
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {SOCIALS.map((s) => (
              <Card
                key={s.label}
                component={Link}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                sx={{
                  display: 'flex', alignItems: 'center', p: 2, gap: 2,
                  borderRadius: 3, boxShadow: 1, textDecoration: 'none',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
                }}
              >
                {s.icon}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                    {s.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {s.sub}
                  </Typography>
                </Box>
              </Card>
            ))}
          </Box>
        </Grid>

        {/* Colonna destra – form */}
        <Grid item xs={12} md={8}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {t('contact.formTitle')}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {status === 'success' && (
            <Alert severity="success" sx={{ mb: 3 }}>{t('contact.successMsg')}</Alert>
          )}
          {status === 'error' && (
            <Alert severity="error" sx={{ mb: 3 }}>{t('contact.errorMsg')}</Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t('contact.name')} name="name"
                  value={form.name} onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t('contact.email')} name="email" type="email" required
                  value={form.email} onChange={handleChange}
                  error={!!errors.email} helperText={errors.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t('contact.phone')} name="phone" type="tel"
                  value={form.phone} onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth multiline rows={{ xs: 3, sm: 4 }} label={t('contact.message')} name="message"
                  value={form.message} onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="gdpr" checked={form.gdpr} onChange={handleChange}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t('contact.gdprLabel')}{' '}
                      <Link href="https://www.garanteprivacy.it/regolamentoue" target="_blank" rel="noopener noreferrer">
                        {t('contact.gdprLink')}
                      </Link>
                    </Typography>
                  }
                />
                {errors.gdpr && (
                  <FormHelperText error>{errors.gdpr}</FormHelperText>
                )}
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit" variant="contained" size="large"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                  sx={{ px: 5 }}
                >
                  {loading ? t('contact.sending') : t('contact.send')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ContactPage;
